import { useEffect, useRef, useState } from "react";
import { styles } from "../styles";
import { wantsAlert } from "../utils/capabilities";
import { notificationEvents } from "../utils/constants";

// Quick lookup: which events this role is even allowed to see.
const eventAvailableTo = Object.fromEntries(
  notificationEvents.map((e) => [e.key, e.availableTo || null])
);
const roleCanSeeEvent = (role, eventKey) => {
  const allowed = eventAvailableTo[eventKey];
  return !allowed || allowed.includes(role);
};

const relativeTime = (iso) => {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
};

/**
 * In-app alerts bell. Shows an unread count badge and toggles a dropdown
 * panel listing the most recent notifications (newest first). Clicking a
 * notification marks it read.
 */
export default function NotificationsBell({
  notifications = [],
  uid,
  role,
  notificationPreferences = {},
  dismissedNotificationIds = [],
  onMarkRead,
  onDismiss,
  onNavigate,
  onReload,
  onOpenSettings,
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close the dropdown on any click outside the bell + its dropdown.
  // Same pattern HeaderMenu uses.
  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const dismissedSet = new Set(dismissedNotificationIds);
  // Four filters:
  //   1) role must be allowed to subscribe to this event type (techs only
  //      get a narrow set; admins/managers see all).
  //   2) personal-target events (targetUids set) must include MY uid.
  //   3) user's own pref must allow this type (default per-role via
  //      wantsAlert — admin/manager default-on, tech default-off).
  //   4) not already cleared by this user.
  const visible = notifications.filter((n) => {
    if (dismissedSet.has(n.id)) return false;
    if (!roleCanSeeEvent(role, n.type)) return false;
    if (Array.isArray(n.targetUids) && n.targetUids.length > 0) {
      if (!uid || !n.targetUids.includes(uid)) return false;
    }
    return wantsAlert(role, notificationPreferences, n.type);
  });
  const sorted = [...visible].sort((a, b) =>
    (b.createdAt || "").localeCompare(a.createdAt || "")
  );
  const unread = sorted.filter((n) => !n.read).length;

  return (
    <div
      ref={ref}
      style={{ position: "relative", display: "inline-block" }}
    >
      <button
        style={{
          ...styles.button,
          padding: "6px 10px",
          lineHeight: 0,
          position: "relative",
        }}
        onClick={() => setOpen((o) => !o)}
        aria-label="Notifications"
        title="Alerts"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M12 2a2 2 0 0 0-2 2v.6A7 7 0 0 0 5 11v3.2L3.4 17a1 1 0 0 0 .9 1.5h15.4a1 1 0 0 0 .9-1.5L19 14.2V11a7 7 0 0 0-5-6.4V4a2 2 0 0 0-2-2zm0 19a2.5 2.5 0 0 0 2.45-2h-4.9A2.5 2.5 0 0 0 12 21z" />
        </svg>
        {unread > 0 && (
          <span
            style={{
              position: "absolute",
              top: "-4px",
              right: "-4px",
              background: "#d12c2c",
              color: "white",
              borderRadius: "10px",
              padding: "0 6px",
              fontSize: "11px",
              fontWeight: "bold",
              lineHeight: "16px",
            }}
          >
            {unread}
          </span>
        )}
      </button>
      {open && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            right: 0,
            marginTop: "4px",
            width: "320px",
            maxHeight: "420px",
            overflowY: "auto",
            background: "white",
            border: "1px solid #ccc",
            borderRadius: "6px",
            boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              padding: "8px 10px",
              borderBottom: "1px solid #eee",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <strong style={{ fontSize: "13px" }}>Alerts</strong>
            <span style={{ display: "flex", gap: "4px" }}>
              {unread > 0 && onMarkRead && (
                <button
                  onClick={() => {
                    sorted
                      .filter((n) => !n.read)
                      .forEach((n) => onMarkRead(n.id));
                  }}
                  style={{
                    fontSize: "11px",
                    padding: "2px 6px",
                    cursor: "pointer",
                  }}
                  title="Mark every alert read"
                >
                  Mark all read
                </button>
              )}
              {sorted.length > 0 && onDismiss && (
                <button
                  onClick={() => {
                    if (
                      window.confirm(
                        "Clear all alerts from your bell? (Only affects you — other people in your org keep theirs.)"
                      )
                    ) {
                      onDismiss(sorted.map((n) => n.id));
                    }
                  }}
                  style={{
                    fontSize: "11px",
                    padding: "2px 6px",
                    cursor: "pointer",
                  }}
                  title="Clear all alerts from your view"
                >
                  Clear
                </button>
              )}
              {onOpenSettings && (
                <button
                  onClick={() => {
                    setOpen(false);
                    onOpenSettings();
                  }}
                  style={{
                    fontSize: "11px",
                    padding: "2px 6px",
                    cursor: "pointer",
                  }}
                  title="Choose which alerts you receive"
                >
                  Settings
                </button>
              )}
              {onReload && (
                <button
                  onClick={onReload}
                  style={{
                    fontSize: "11px",
                    padding: "2px 6px",
                    cursor: "pointer",
                  }}
                  title="Reconnect if alerts aren't updating"
                >
                  Reload
                </button>
              )}
            </span>
          </div>
          {sorted.length === 0 ? (
            <div
              style={{ padding: "16px", fontSize: "12px", color: "#666" }}
            >
              No alerts yet.
            </div>
          ) : (
            sorted.slice(0, 30).map((n) => (
              <div
                key={n.id}
                onClick={() => {
                  if (!n.read) onMarkRead?.(n.id);
                  if (n.link && onNavigate) {
                    setOpen(false);
                    onNavigate(n.link);
                  }
                }}
                style={{
                  padding: "8px 10px",
                  borderBottom: "1px solid #f0f0f0",
                  background: n.read ? "white" : "#f4f8ff",
                  cursor: n.link || !n.read ? "pointer" : "default",
                  fontSize: "12px",
                }}
              >
                <div style={{ fontWeight: n.read ? "normal" : "bold" }}>
                  {n.title || n.type}
                </div>
                {n.body && (
                  <div style={{ color: "#444", marginTop: "2px" }}>
                    {n.body}
                  </div>
                )}
                <div
                  style={{ color: "#888", marginTop: "4px", fontSize: "11px" }}
                >
                  {relativeTime(n.createdAt)}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
